import React from 'react';
import { Button, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { useAppLifecycle, type AppStatus } from '../../hooks/useAppLifecycle';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';

interface AppLifecycleControlProps {
  projectId: string | undefined;
  /** The current user's permissions on this project. Without them the control
   *  rendered for everyone, so a viewer saw a button whose endpoint answers
   *  403. */
  permissions?: string[];
}

const STATUS_CONFIG: Record<
  AppStatus,
  { icon: IconName; label: string; spin?: boolean }
> = {
  stopped:    { icon: IconName.Run,       label: 'Start App' },
  starting:   { icon: IconName.Loading,   label: 'Starting...', spin: true },
  running:    { icon: IconName.Stop,      label: 'Stop App' },
  stopping:   { icon: IconName.Loading,   label: 'Stopping...', spin: true },
  // A partly running app is stopped by the button (handleClick), so it says so.
  partial:    { icon: IconName.Stop,      label: 'Stop App' },
  error:      { icon: IconName.Run,       label: 'Start App' },
  no_compose: { icon: IconName.Container, label: 'No Docker Compose' },
  unknown:    { icon: IconName.Container, label: 'Checking...' },
};

const AppLifecycleControl: React.FC<AppLifecycleControlProps> = ({
  projectId,
  permissions,
}) => {
  const { state, startApp, stopApp, isTransitioning } = useAppLifecycle(projectId);

  const config = STATUS_CONFIG[state.status] ?? STATUS_CONFIG.unknown;

  const handleClick = async () => {
    if (isTransitioning) return;
    if (state.status === 'running' || state.status === 'partial') {
      await stopApp();
    } else if (
      state.status === 'stopped' ||
      state.status === 'error' ||
      state.status === 'unknown'
    ) {
      await startApp();
    }
  };

  if (!projectId || state.status === 'no_compose') {
    return null;
  }

  return (
    // `allowAdmin`, matching the endpoint: /app/start and /app/stop both carry
    // `allow_admin=True`, so an admin may run them even though their project
    // permissions are read-only. A viewer may not, and now does not see it.
    <IfHasPermission
      permissions={permissions}
      requires={Permissions.UpdateProjectPermission}
      allowAdmin
    >
    <Button
      label={
        <Icon
          name={config.icon}
          size='sm'
          className='text-fg-secondary'
          spin={config.spin}
          inheritColor
        />
      }
      variant='ghost'
      size='small'
      title={config.label}
      className='!rounded-full !p-bg-v2-lg'
      onClick={handleClick}
      disabled={isTransitioning}
    />
    </IfHasPermission>
  );
};

export default AppLifecycleControl;
